import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Layers, Plus, Trash2, FileText, Edit2 } from 'lucide-react';

export default function CustomFrameworkPage() {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showControlDialog, setShowControlDialog] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    version: '1.0',
    controls: []
  });
  const [controlData, setControlData] = useState({
    code: '',
    title: '',
    description: '',
    category: 'General'
  });

  useEffect(() => {
    fetchFrameworks();
  }, []);

  const fetchFrameworks = async () => {
    try {
      const response = await api.get('/frameworks/custom');
      setFrameworks(response.data || []);
    } catch (error) {
      // Custom frameworks might not exist yet
      setFrameworks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFramework = async (e) => {
    e.preventDefault();
    try {
      await api.post('/frameworks/custom', formData);
      toast.success('Custom framework created successfully');
      setShowDialog(false);
      setFormData({ name: '', description: '', version: '1.0', controls: [] });
      fetchFrameworks();
    } catch (error) {
      toast.error('Failed to create framework');
    }
  };

  const handleAddControl = () => {
    if (!controlData.title) {
      toast.error('Control title is required');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      controls: [...prev.controls, { ...controlData }]
    }));
    
    setControlData({
      code: '',
      title: '',
      description: '',
      category: 'General'
    });
    setShowControlDialog(false);
  };

  const removeControl = (idx) => {
    setFormData(prev => ({
      ...prev,
      controls: prev.controls.filter((_, i) => i !== idx)
    }));
  };

  const handleAddControlToExisting = async () => {
    if (!selectedFramework || !controlData.title) return;
    
    try {
      await api.post(`/frameworks/custom/${selectedFramework.id}/controls`, controlData);
      toast.success('Control added to framework');
      setShowControlDialog(false);
      setSelectedFramework(null);
      setControlData({ code: '', title: '', description: '', category: 'General' });
      fetchFrameworks();
    } catch (error) {
      toast.error('Failed to add control');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader
        title="Custom Frameworks"
        subtitle="Build and manage your own compliance frameworks"
        icon={<Layers size={28} />}
        actions={
          <Button
            data-testid="create-framework-btn"
            onClick={() => setShowDialog(true)}
            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
          >
            <Plus size={18} className="mr-2" />
            Create Framework
          </Button>
        }
      />

      {/* Existing Custom Frameworks */}
      {frameworks.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {frameworks.map((fw) => (
            <div key={fw.id} className="bg-white border border-slate-200 rounded-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg">{fw.name}</h3>
                  <p className="text-sm text-slate-500">Version {fw.version}</p>
                </div>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                  Custom
                </span>
              </div>
              <p className="text-sm text-slate-600 mb-4 line-clamp-2">{fw.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  {fw.control_count || 0} controls
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setSelectedFramework(fw);
                    setShowControlDialog(true);
                  }}
                  className="rounded-sm"
                >
                  <Plus size={14} className="mr-1" />
                  Add Control
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-slate-50 rounded-sm">
          <Layers size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="font-semibold text-lg mb-2">No Custom Frameworks</h3>
          <p className="text-slate-500 mb-4">
            Create your first custom compliance framework to track specific requirements.
          </p>
          <Button onClick={() => setShowDialog(true)} className="rounded-sm">
            <Plus size={18} className="mr-2" />
            Create Framework
          </Button>
        </div>
      )}

      {/* Create Framework Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary">Create Custom Framework</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFramework} className="space-y-4 mt-4">
            <div>
              <Label>Framework Name *</Label>
              <Input
                data-testid="framework-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Internal Security Policy v2"
                required
                className="mt-1.5 rounded-sm"
              />
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea
                data-testid="framework-desc-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the purpose and scope of this framework..."
                required
                rows={3}
                className="mt-1.5 rounded-sm"
              />
            </div>
            
            <div>
              <Label>Version</Label>
              <Input
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="1.0"
                className="mt-1.5 rounded-sm"
              />
            </div>

            {/* Controls Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <Label>Controls ({formData.controls.length})</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowControlDialog(true)}
                  className="rounded-sm"
                >
                  <Plus size={14} className="mr-1" />
                  Add Control
                </Button>
              </div>
              
              {formData.controls.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.controls.map((ctrl, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                      <div>
                        <span className="font-mono text-sm font-medium text-slate-700 mr-2">
                          {ctrl.code || `CTRL-${idx + 1}`}
                        </span>
                        <span className="text-sm">{ctrl.title}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeControl(idx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-sm">
                  No controls added yet. You can add them now or after creating the framework.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-framework-btn"
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              >
                Create Framework
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Control Dialog */}
      <Dialog open={showControlDialog} onOpenChange={() => {
        setShowControlDialog(false);
        setSelectedFramework(null);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-secondary">
              {selectedFramework ? `Add Control to ${selectedFramework.name}` : 'Add Control'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Control Code</Label>
              <Input
                data-testid="control-code-input"
                value={controlData.code}
                onChange={(e) => setControlData({ ...controlData, code: e.target.value })}
                placeholder="e.g., SEC-001"
                className="mt-1.5 rounded-sm"
              />
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                data-testid="control-title-input"
                value={controlData.title}
                onChange={(e) => setControlData({ ...controlData, title: e.target.value })}
                placeholder="e.g., Password Policy Enforcement"
                required
                className="mt-1.5 rounded-sm"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={controlData.description}
                onChange={(e) => setControlData({ ...controlData, description: e.target.value })}
                placeholder="Describe the control requirements..."
                rows={3}
                className="mt-1.5 rounded-sm"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Input
                value={controlData.category}
                onChange={(e) => setControlData({ ...controlData, category: e.target.value })}
                placeholder="e.g., Access Control"
                className="mt-1.5 rounded-sm"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowControlDialog(false);
                  setSelectedFramework(null);
                }}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={selectedFramework ? handleAddControlToExisting : handleAddControl}
                data-testid="add-control-btn"
                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              >
                Add Control
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
